import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography
} from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import EditIcon from '@mui/icons-material/Edit';
import type { Robot } from '@api/bff/stationDevices';
import {
  DEFAULT_MASTER_SETUP,
  DEFAULT_MASTER_TEARDOWN
} from '../constants';

type Props = {
  robot: Robot | null;
  onEdit: (robot: Robot) => void;
};

type StepLike = { action?: string; label?: string; durationMs?: number };

const describeStep = (step: StepLike): string => {
  if (step.action) return step.action;
  if (step.durationMs) return `Wait ${step.durationMs / 1000}s`;
  return step.label || 'Step';
};

const StepSequence = ({
  steps,
  isDefault
}: {
  steps: StepLike[];
  isDefault: boolean;
}) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
    {steps.map((step, index) => (
      <Chip
        key={`${describeStep(step)}-${index}`}
        label={describeStep(step)}
        size="small"
        variant={isDefault ? 'outlined' : 'filled'}
        sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}
      />
    ))}
  </Box>
);

/**
 * Shows the active robot's power-up / power-down sequences on the Station Devices page itself.
 *
 * The builder's "Station power settings" link lands here, and these sequences previously lived only
 * inside the per-robot edit dialog — so the page looked like it had no power settings at all. They
 * are also what Fresh/Warm start switches on, which is why the explanation lives next to them.
 */
const PowerSettingsCard: React.FC<Props> = ({ robot, onEdit }) => {
  const setupSteps = robot?.setupSteps?.length
    ? (robot.setupSteps as StepLike[])
    : (DEFAULT_MASTER_SETUP as StepLike[]);
  const teardownSteps = robot?.teardownSteps?.length
    ? (robot.teardownSteps as StepLike[])
    : (DEFAULT_MASTER_TEARDOWN as StepLike[]);

  const setupIsDefault = !robot?.setupSteps?.length;
  const teardownIsDefault = !robot?.teardownSteps?.length;

  return (
    <Card sx={{ mt: 3 }} id="power-settings">
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PowerSettingsNewIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Station power settings
            </Typography>
          </Box>
          {robot && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => onEdit(robot)}
            >
              Edit
            </Button>
          )}
        </Box>

        {!robot ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            No robot is configured for this station yet. Add one above — until
            then a run cannot power up the rig, and re-home has nothing to talk
            to.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary">
              These run against <strong>{robot.label || robot.stationId}</strong>
              . The <strong>station</strong> owns powering the rig up and down —
              individual calibration scripts must not, or the rig powers up
              twice and re-homes mid-run.
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Master Setup
                </Typography>
                {setupIsDefault && (
                  <Chip label="default" size="small" color="info" />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Runs once at the start of a run, before the first pickup —
                unless the run is launched Warm.
              </Typography>
              <StepSequence steps={setupSteps} isDefault={setupIsDefault} />
            </Box>

            <Box sx={{ mt: 2.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Master Teardown
                </Typography>
                {teardownIsDefault && (
                  <Chip label="default" size="small" color="info" />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Always runs at the end, even if the run fails or is aborted.
                Never skipped.
              </Typography>
              <StepSequence
                steps={teardownSteps}
                isDefault={teardownIsDefault}
              />
            </Box>

            {(setupIsDefault || teardownIsDefault) && (
              <Alert severity="info" sx={{ mt: 2.5 }}>
                Sequences marked <strong>default</strong> are not saved on this
                robot — the backend substitutes them at run time. Open Edit and
                save to make them explicit.
              </Alert>
            )}

            <Divider sx={{ my: 2.5 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Fresh vs Warm start
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 2.5,
                m: 0,
                '& li': {
                  mb: 1,
                  color: 'text.secondary',
                  fontSize: '0.875rem'
                },
                '& li strong': { color: 'text.primary' }
              }}
            >
              <li>
                <strong>Fresh</strong> — runs Master Setup first. Use it for the
                first run of a session, or any time the rig has been powered off
                or the arm may not be homed. This is the default, including for
                older runs that never recorded a choice.
              </li>
              <li>
                <strong>Warm</strong> — skips Master Setup, assuming the rig is
                already powered and homed from an earlier run. Saves the homing
                cycle (~4 minutes). Only safe when a previous run in the same
                session completed normally.
              </li>
              <li>
                <strong>Re-home</strong> — replays Master Setup on demand, for
                when an abort has left the arm in an unknown position. Refused
                while a run is executing.
              </li>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PowerSettingsCard;
